import { z } from 'zod';

import { AuditorRuleOverridesSchema, PendingAutoRatifyPolicySchema } from './campaign-policy.js';
import { FacilitationModeSchema } from './facilitation.js';
import { GmPrefProfileSchema } from './gm-pref-profile.js';
import { DimensionIdSchema, UnitIdSchema } from './predicate.js';

/**
 * Campaign-settings op shape (#169, ADR-0004 §5 — op-log config-ops).
 *
 * Campaign-level settings are NOT facts. They live in the "config" bucket
 * of the three-bucket model (ADR-0004 §4): a fixed, code-typed set of
 * keys written to the op log as `config.set` ops under a reserved subject.
 * Unlike facts, config ops carry **no** reveal, validity, or belief
 * fields — a setting is engine-scoped, single-valued, last-writer-wins,
 * and visible to every member. The payload schema is `.strict()` precisely
 * so those fact-only fields can never leak onto a config op.
 *
 * This module is the wire/shape + typed-schema layer only:
 *   - the op payload `{ kind: 'config.set', key, value }`,
 *   - the reserved subject constant,
 *   - the fixed Zod config-key schema (each key validated to its type).
 *
 * The fold into a `config` record (#171), the DO handling (#176), and the
 * Settings UI (#186) are separate tickets and deliberately NOT here.
 */

/**
 * The reserved subject id every campaign-config op is written under. Like
 * `HOMEBREW_PACK_ID` for `__pack` meta-ops, the `__`-prefix keeps these
 * ops out of the user-addressable entity namespace; a campaign has exactly
 * one config subject so the settings are addressable as a single chain.
 */
export const CAMPAIGN_CONFIG_SUBJECT = '__campaign';

// ─── Per-key value vocabularies ────────────────────────────────────────
//
// Some of these mirror declarations that already exist elsewhere (core.json
// predicates, the campaign HTTP surface). They are re-stated here as the
// canonical settings-bucket schema because the config bucket is a fixed,
// code-typed contract independent of the user-declarable predicate registry.

/**
 * Prose-voice presets (mirrors core.json's `prose_voice` enum and the
 * worker's `ProseVoiceSchema`). Drives the LLM tone for generated prose.
 */
export const ConfigProseVoiceSchema = z.enum([
  'default',
  'gritty',
  'whimsical',
  'pulp',
  'noir',
  'high_fantasy',
]);
export type ConfigProseVoice = z.infer<typeof ConfigProseVoiceSchema>;

/**
 * Campaign-kind presets. Closed enum at the config-bucket layer (the
 * legacy create-campaign HTTP field was free-form text; the settings
 * bucket pins it to a vocabulary). Shape/playtime classification only —
 * it never gates a feature.
 */
export const ConfigCampaignKindSchema = z.enum([
  'one_shot',
  'mini_arc',
  'long_running',
  'sandbox',
  'episodic',
]);
export type ConfigCampaignKind = z.infer<typeof ConfigCampaignKindSchema>;

/** Campaign lifecycle status. `active` is the default; `archived` is read-mostly. */
export const ConfigCampaignStatusSchema = z.enum(['active', 'archived']);
export type ConfigCampaignStatus = z.infer<typeof ConfigCampaignStatusSchema>;

/**
 * An sRGB hex color: `#` followed by 3, 4, 6, or 8 hex digits (RGB / RGBA /
 * RRGGBB / RRGGBBAA).
 *
 * TODO(#168): switch to the shared color validator. #168 (the `color` value
 * kind) is landing in parallel; this inline regex is a deliberate local
 * stand-in so #169 does not block on #168. When the shared validator ships,
 * replace `AccentColorSchema` with it (keep the same accepted forms).
 */
const HEX_COLOR_RE = /^#(?:[0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;
export const AccentColorSchema = z
  .string()
  .regex(HEX_COLOR_RE, 'accent_color must be an sRGB hex value, e.g. #1a2b3c');

/** An asset-store identifier (opaque string id in Phase 1). */
const AssetIdSchema = z.string().min(1);

/** A calendar id (resolved against bundled + pack calendars at use time). */
const CalendarIdSchema = z.string().min(1);

// ─── LLM pending-lane settings (#223, 04-llm-role.md §4.1.1) ───────────
//
// The lane lets allowlisted LLM-authored drafts auto-promote to `pending` for
// mid-session GM visibility WITHOUT ever weakening "the LLM never writes canon"
// (the LLM still only ever authors `status:'draft'`; promotion is a separate
// `system`-authored `fact.set_status` in the same DO turn — see
// `apps/do-campaign/src/append.ts`). All three keys default to lane-off.

/**
 * A predicate **opaque id** eligible for the LLM pending lane (#223). Parsed
 * **tolerantly** as a non-empty string — NOT pinned to the `pred_…` minted
 * shape — for two deliberate reasons:
 *   1. the built-in `name` predicate id is the bare literal `'name'` (it has no
 *      `pred_` prefix), and
 *   2. a future widening of the allowlist vocabulary (e.g. per-entity-root
 *      scoping entries) must fold non-destructively under LWW rather than have
 *      the whole array rejected by a stricter element schema.
 * Eligibility is resolved through the folded `alias_vocab`/retire `aliases` map
 * at promotion time, and built-in `name` + reserved `__pack`/`__campaign` are
 * never eligible regardless of allowlist membership (enforced in the DO).
 */
const LlmPendingPredicateIdSchema = z.string().min(1);

/**
 * The LLM pending-lane mode (#223). Replaces the issue's
 * `llm_pending_silent_auto` boolean (which conflated "is the lane active" with
 * "does it notify"):
 *   - `off`     — lane inactive; eligible drafts still land in the draft inbox
 *                 (lets a GM keep a curated allowlist but kill the lane for a
 *                 session).
 *   - `surface` — auto-promote AND visibly increment the session-HUD pending
 *                 count.
 *   - `silent`  — auto-promote with NO notification (the live-play
 *                 "don't interrupt me" mode).
 * Neither active mode ever implies ratification (→canon stays GM-only).
 */
export const LlmPendingLaneModeSchema = z.enum(['off', 'surface', 'silent']);
export type LlmPendingLaneMode = z.infer<typeof LlmPendingLaneModeSchema>;

/** The default lane mode — lane OFF (#223). */
export const DEFAULT_LLM_PENDING_LANE_MODE: LlmPendingLaneMode = 'off';

/**
 * An entity id opted OUT of the LLM pending lane (#223 `gm_only_drafts`). Parsed
 * tolerantly (non-empty string) — entity roots + minted ids share no single
 * prefix and the door stays open for future scoping. Membership keeps that
 * entity's LLM drafts GM-only in the inbox (never auto-promoted), for sensitive
 * entities.
 */
const GmOnlyDraftEntityIdSchema = z.string().min(1);

/**
 * The session cursor — the campaign's in-world "now" (06-timeline.md §13.1,
 * ADR-0005 §"Session cursor"). A canonical day count; both `null` means
 * "no in-world date set" (timeless).
 *
 * This is a **setting**, not a fact: it carries no reveal / validity / belief
 * and is engine-scoped + last-writer-wins. Riding the op log as a `config.set`
 * (rather than the prior D1 column + ephemeral WS relay) buys live multi-GM
 * sync of "now" over the same WS the rest of canon flows through, and makes the
 * value op-log-canonical + replay-safe; the prior `campaign_runtime`/D1 field
 * demotes to a rebuildable cache. The high-frequency *browse* cursor stays
 * ephemeral client state and never rides this key (ADR-0005).
 *
 * ADR-0006 (governing truth): the canonical day axis is SHARED and
 * calendar-independent — every calendar is a bijection onto the same integer
 * line. So `day` is meaningful on its own, across any calendar; switching the
 * campaign's canonical calendar does NOT invalidate it (the same day simply
 * re-renders in the new calendar). `calendar_id` is retained as *provenance*
 * (which calendar the value was authored against, and the default calendar for
 * seeding fact date-pickers), not as a coordinate system.
 *
 * The value is **`{ day, calendar_id }` or `null`** — never a half-set object.
 * A set cursor always carries BOTH a day and the calendar it was authored in;
 * `null` is the single representation of "no in-world date set" (timeless).
 * Modelling it as a nullable whole (rather than two independently-nullable
 * fields + a refine) makes the half-set states unrepresentable by the type.
 */
export const SessionCursorValueSchema = z
  .object({
    day: z.number().int(),
    calendar_id: CalendarIdSchema,
  })
  .strict()
  .nullable();
export type SessionCursorValue = z.infer<typeof SessionCursorValueSchema>;

/**
 * The four prompt-extension scopes a GM can author (ADR-0004 §5 / #186): a
 * campaign-wide `global` note appended to every LLM task, plus one optional
 * per-task extension for each task (`prose_suggest`, `draft_entity`,
 * `image_generate`). These mirror the worker prompt composer's `PromptTask`
 * scopes and replace the dropped `campaign_ai_settings` D1 table — the
 * extensions now ride the op log as a single `ai_prompt_extensions` config
 * value (one object, last-writer-wins, folded into the campaign config bucket).
 */
export const AI_PROMPT_EXTENSION_SCOPES = [
  'global',
  'prose_suggest',
  'draft_entity',
  'image_generate',
] as const;
export type AiPromptExtensionScope = (typeof AI_PROMPT_EXTENSION_SCOPES)[number];

/**
 * Per-campaign LLM prompt extensions, keyed by scope. Every scope is optional
 * (an unset scope contributes nothing) and the object is `.strict()` so an
 * unknown scope is rejected — the worker composer routes by scope, so a stray
 * key would otherwise be a silent no-op. These are free-text model
 * instructions, NOT user-facing UI strings; they never flow through the i18n
 * message layer (matching the worker's `prompts.ts` policy).
 */
export const AiPromptExtensionsSchema = z
  .object({
    global: z.string().optional(),
    prose_suggest: z.string().optional(),
    draft_entity: z.string().optional(),
    image_generate: z.string().optional(),
  })
  .strict();
export type AiPromptExtensions = z.infer<typeof AiPromptExtensionsSchema>;

// ─── Unit display defaults (#249, 08-units.md §5 / ADR-0007 §5) ────────
//
// Which unit each dimension RENDERS in is a SETTING, not a definition — a
// per-dimension pointer holding a `unit_…` id, exactly like `display_calendars`
// points at calendar definitions (ADR-0004 three-bucket: the exportable thing
// is always a definition, never a setting). The campaign default rides this
// config key; a user's per-dimension override is User-scope (not campaign
// config — §5), resolved client-side over this default. The stored
// `canonical_value` is never touched; only the rendered unit converts (via
// `units.ts` `resolveDisplayUnit` + `convert`). Export carries canonical
// values, never the display projection (ADR-0007).

/**
 * A per-dimension display-unit map: `{ [dimension_id: dim_…]: unit_id: unit_… }`.
 * Keys are opaque dimension ids, values opaque unit ids — both validated to
 * their minted shapes. An absent dimension means "no campaign default" (render
 * in the fact's provenance unit). A stale/cross-dimension pointer is ignored at
 * render time by `resolveDisplayUnit` (defence-in-depth), so the fold accepts
 * any well-shaped map without cross-referencing the live registry.
 */
export const UnitDisplayDefaultsSchema = z.record(DimensionIdSchema, UnitIdSchema);
export type UnitDisplayDefaults = z.infer<typeof UnitDisplayDefaultsSchema>;

// ─── Rotating-facilitator settings (#265, 07-session-experience.md §2.2) ──
//
// `session_facilitator` is the user_id of the campaign member who holds the
// facilitator badge for the current session. `null` means no facilitator has
// been designated yet (session not started, or the seat is vacant). In the DO
// gate, `rotating` mode resolves the canon-write authority against THIS value:
// only the current facilitator may ratify / move the cursor.
//
// `rotation_history` is an append-only ordered log of every facilitator
// assignment — one entry per session a facilitator was designated. The fold is
// LWW (the whole array replaces on write), so the client sends the FULL
// cumulative list on every hand-off / session-start. The array is ordered
// oldest-first; the last entry matches `session_facilitator` when a session is
// active. Kept deliberately simple (no separate session-id join) because the
// HUD only needs per-member counts and the most-recent entry.

/** One rotation-history entry: a facilitator badge assignment for one session. */
export const RotationHistoryEntrySchema = z
  .object({
    /** The user_id who held (or holds) the facilitator badge. */
    user_id: z.string().min(1),
    /** ISO-8601 real-world wall-clock timestamp of the assignment (informational). */
    assigned_at: z.string().min(1),
  })
  .strict();
export type RotationHistoryEntry = z.infer<typeof RotationHistoryEntrySchema>;

/**
 * The fixed, code-typed campaign-config schema (ADR-0004 §5). Every key
 * is enumerated here with its value type; this object is the single source
 * of truth for "what settings exist and what shape each holds". It is
 * `.strict()`-ready (`ConfigSetPayloadSchema` enforces the per-key value
 * type and rejects unknown keys via the union below).
 */
export const CampaignConfigSchema = z.object({
  /** Display name of the world the campaign is set in. */
  world_name: z.string().min(1),
  /** sRGB hex accent color for the campaign's theming. */
  accent_color: AccentColorSchema,
  /** Asset id of the campaign hero image. */
  hero_image: AssetIdSchema,
  /** LLM prose-voice preset. */
  prose_voice: ConfigProseVoiceSchema,
  /** Markdown/rich-text setting blurb (rich_text value kind — stored as a string). */
  setting_summary: z.string(),
  /** Campaign-kind classification. */
  campaign_kind: ConfigCampaignKindSchema,
  /** The canonical in-world calendar id (drives the campaign-date cursor). */
  canonical_calendar: CalendarIdSchema,
  /** Additional calendar ids shown alongside the canonical one. */
  display_calendars: z.array(CalendarIdSchema),
  /** BCP-47 locale tag for date/number/unit formatting. */
  locale: z.string().min(1),
  /** IANA time-zone id for real-world wall-clock display. */
  time_zone: z.string().min(1),
  /** Campaign lifecycle status. */
  status: ConfigCampaignStatusSchema,
  /**
   * The facilitation mode (07-session-experience.md §2.2 / #256). Decides who
   * ratifies, who moves the session cursor, and whether member writes auto-canon
   * — resolved by the pure `resolveFacilitationPolicy` chokepoint the DO append
   * gate enforces. A SETTING (ADR-0004 §5): the op log is the single source of
   * truth; the D1 `facilitation_mode` column is demoted to a rebuildable cache.
   * Orthogonal to `pending_auto_ratify_policy` — the mode decides author-time
   * canon-eligibility; the policy decides the pending-lane exit at session close.
   */
  facilitation_mode: FacilitationModeSchema,
  /**
   * The user_id of the campaign member currently holding the facilitator badge
   * (#265, 07-session-experience.md §2.2 `rotating` mode). `null` = no active
   * facilitator designated. In `rotating` mode the DO gate resolves canon-write
   * authority against this value: the member whose user_id matches here is the
   * sole ratifier + cursor authority for the current session.
   *
   * Set by the campaign owner at session-start setup, or via mid-session
   * hand-off (by the *current* facilitator). `null` when no session is active
   * or the facilitator seat is vacant. Has no effect in other modes.
   */
  session_facilitator: z.string().nullable(),
  /**
   * Append-only ordered log of facilitator assignments (#265). The FULL list
   * (oldest-first) is written on every assignment; LWW per the config fold.
   * Each entry carries the `user_id` + `assigned_at` ISO timestamp. The HUD
   * derives per-member counts from this list for the fairness display.
   * Intentionally a simple flat array — no separate session-id join needed for
   * the count display.
   */
  rotation_history: z.array(RotationHistoryEntrySchema),
  /** Pending-fact auto-ratify policy (reused from campaign-policy). */
  pending_auto_ratify_policy: PendingAutoRatifyPolicySchema,
  /** Per-rule auditor severity overrides (reused from campaign-policy). */
  auditor_rule_overrides: AuditorRuleOverridesSchema,
  /**
   * The session cursor — the campaign's in-world "now" (ADR-0005). Either
   * `{ day, calendar_id }` or `null` (no date set). The shared-axis `day` is
   * canonical (ADR-0006); `calendar_id` is its authoring-calendar provenance.
   * LWW like every config key; co-GMs see "now" move live as the op folds in.
   */
  session_cursor: SessionCursorValueSchema,
  /**
   * Per-campaign LLM prompt extensions (ADR-0004 §5 / #186): the GM-authored
   * `global` note + optional per-task notes the worker composer appends to the
   * admin global templates. One object value — last-writer-wins as a whole.
   */
  ai_prompt_extensions: AiPromptExtensionsSchema,
  // ─── Per-campaign LLM cost-control overrides (#217 / #218) ────────────
  //
  // Optional per-campaign overrides of the Account-wide / global LLM
  // cost-control policy (#216). Each is a setting, not a fact: LWW, no
  // reveal/validity/belief, GM-scoped. When unset, the worker AI Gateway
  // falls back to the global admin-configured default (seeded with the #216
  // values — `llm_global_settings`). Resolution + enforcement live entirely in
  // the worker (`apps/worker/src/ai/policy.ts`); these keys are only the
  // GM-authored override surface, projected to D1 so the gateway reads them
  // cheaply (see `extractLlmOverrides`).
  /**
   * Per-campaign GM **kill switch** (#218). `true` refuses every LLM task for
   * this campaign at the gateway before any provider call or spend — it takes
   * precedence over budget/rate state. Scopes the stop to one campaign (the
   * Account-wide admin kill switch is a separate global setting).
   */
  llm_kill_switch: z.boolean(),
  /** Per-campaign monthly budget cap override, in integer micro-USD (USD×1e6). */
  llm_budget_micro_usd: z.number().int().nonnegative(),
  /**
   * Per-campaign "unlimited budget" override. Only honoured when the global
   * admin `unlimited_allowed` flag is on (#216 §2) — otherwise ignored and the
   * finite cap still applies.
   */
  llm_budget_unlimited: z.boolean(),
  /** Per-campaign per-minute rate-limit override (the runaway-loop catch). */
  llm_rate_per_minute: z.number().int().nonnegative(),
  /** Per-campaign per-hour rate-limit override. */
  llm_rate_per_hour: z.number().int().nonnegative(),
  /** Per-campaign per-day rate-limit override. */
  llm_rate_per_day: z.number().int().nonnegative(),
  // ─── LLM pending lane (#223, 04-llm-role.md §4.1.1) ───────────────────
  //
  // Lets allowlisted LLM-authored drafts auto-promote to `pending` for
  // mid-session GM visibility without ever weakening "the LLM never writes
  // canon". The LLM still only authors `status:'draft'`; the DO appends a
  // separate `system`-authored `fact.set_status draft→pending` in the same
  // single-threaded turn when the full promotion predicate holds
  // (`mode !== 'off'` AND predicate ∈ allowlist (alias-resolved) AND subject ∉
  // gm_only_drafts). Promotion emits ZERO reveal ops, so a promoted pending
  // fact is structurally absent from every non-GM projection — same protection
  // as any unrevealed canon fact (the reveal chokepoint, not a status gate).
  /**
   * The set of predicate **opaque ids** eligible for the LLM pending lane.
   * Default `[]` (lane carries nothing). Eligibility resolves BOTH the incoming
   * fact's predicate id AND each allowlist entry through the folded
   * `alias_vocab`/retire `aliases` map before comparing (a naive `includes`
   * would be a referential-integrity bug — unifying or retiring a predicate
   * must keep the allowlist correct). Built-in `name` + reserved
   * `__pack`/`__campaign` are never eligible regardless (enforced in the DO).
   */
  llm_pending_allowlist: z.array(LlmPendingPredicateIdSchema),
  /**
   * The lane mode: `off` (lane inactive — eligible drafts still land in the
   * draft inbox), `surface` (auto-promote + increment the session-HUD pending
   * count), or `silent` (auto-promote with no notification). Default `off`.
   * Neither active mode implies ratification.
   */
  llm_pending_lane_mode: LlmPendingLaneModeSchema,
  /**
   * Per-entity **opt-out** of the lane. An entity id in this list keeps its
   * LLM drafts GM-only in the inbox (never auto-promoted), for sensitive
   * entities. Default `[]`. A setting, not a fact (ADR-0004 "not all data is
   * facts" — a GM workflow preference, never world-canon, never
   * reveal/validity-bearing, never projected).
   */
  gm_only_drafts: z.array(GmOnlyDraftEntityIdSchema),
  /**
   * Per-campaign default display unit per dimension (#249, 08-units.md §5).
   * `{ [dim_…]: unit_… }`; an absent dimension renders in the fact's provenance
   * unit. A user's per-dimension override is User-scope (not config), layered
   * over this default at render time. The canonical value is never altered.
   */
  unit_display_defaults: UnitDisplayDefaultsSchema,
  // ─── GM preference profile (#250, 04-llm-role.md §16) ─────────────────
  //
  // The EXPLICIT layer of the two-layer GM preference profile: GM-authored
  // style knobs (tone, content boundaries, pacing, naming, vocabulary, style
  // references, free-form notes). A setting, not a fact (ADR-0004): LWW,
  // GM-authored, no reveal/validity/belief, export-clean — and the LLM never
  // writes it (it shapes generation; it is never world-canon). The composed
  // profile (explicit + opt-in-gated inferred) feeds the worker prompt composer
  // alongside `ai_prompt_extensions` (D13). Shape lives in `gm-pref-profile.ts`.
  /** GM-authored explicit preference profile (the always-on layer). */
  gm_pref_profile: GmPrefProfileSchema,
  /**
   * Opt-in gate for the INFERRED layer (#250, §16.3 — "AI is always optional").
   * Default OFF: with this `false` (or unset), signal-derived inferred entries
   * are NEVER composed into the prompt, regardless of what the rebuildable
   * derived store holds. The GM enables it explicitly; revocable any time. This
   * is the single gate guarding "inference is opt-in, off by default".
   */
  gm_pref_inference_enabled: z.boolean(),
  /**
   * Narrative combat panel opt-in (#260, 07-session-experience.md §13.9.5).
   * Off by default — tables that use Foundry leave it off; non-Foundry tables
   * flip it on. When off, the panel is structurally absent from the session HUD.
   * The initiative chip (#257) continues to function regardless of this setting.
   */
  narrative_combat_panel_enabled: z.boolean(),
});
export type CampaignConfig = z.infer<typeof CampaignConfigSchema>;

/**
 * The set of valid config keys, derived from `CampaignConfigSchema` so the
 * key list and the value schema can never drift apart.
 */
export const CONFIG_KEYS = Object.keys(CampaignConfigSchema.shape) as ConfigKey[];
export type ConfigKey = keyof CampaignConfig;

// ─── The config.set op payload ─────────────────────────────────────────

/**
 * One `config.set { kind, key, value }` arm per config key. Building the
 * union from `CampaignConfigSchema.shape` keeps the op shape and the typed
 * schema in lockstep: each arm pins `key` to a literal and `value` to that
 * key's value schema, so an unknown key has no arm (rejected) and a
 * mistyped value fails its arm's value schema (rejected). Every arm is
 * `.strict()`, so no reveal/validity/belief field can ride along.
 */
const configSetArms = (Object.entries(CampaignConfigSchema.shape) as [ConfigKey, z.ZodTypeAny][]).map(
  ([key, valueSchema]) =>
    z
      .object({
        kind: z.literal('config.set'),
        key: z.literal(key),
        value: valueSchema,
      })
      .strict(),
);

export const ConfigSetPayloadSchema = z.union(
  configSetArms as unknown as [z.ZodTypeAny, z.ZodTypeAny, ...z.ZodTypeAny[]],
);

/**
 * A `config.set` op payload for config key `K`. The conditional `value`
 * type gives callers exact-typed values per key (e.g. `prose_voice`'s
 * value is the prose-voice enum, not `string`).
 */
export interface ConfigSetPayload<K extends ConfigKey = ConfigKey> {
  kind: 'config.set';
  key: K;
  value: CampaignConfig[K];
}

// ─── Builder ───────────────────────────────────────────────────────────

/** Build a `config.set` op payload (create or update — last writer wins). */
export function configSet<K extends ConfigKey>(
  key: K,
  value: CampaignConfig[K],
): ConfigSetPayload<K> {
  return { kind: 'config.set', key, value };
}

// ─── Type guard ────────────────────────────────────────────────────────

/** Narrow an unknown op payload to a `config.set` payload (shape-only check). */
export function isConfigSet(payload: unknown): payload is ConfigSetPayload {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    (payload as { kind?: unknown }).kind === 'config.set'
  );
}
